import { useEffect, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import {
  AD_CLIENT,
  adScriptAllowedNow,
  autoAdsAllowedNow,
  personalizedAdsAllowed,
} from "@/lib/ads-policy";
import { ADS_ENABLED, AUTO_ADS_ON } from "@/lib/ads-config";

/**
 * Laddar AdSense-skriptet FÖRST när sidan klarar annonspolicyn
 * (se src/lib/ads-policy.ts): master-brytaren är på, cookie-beslut finns,
 * sidan är indexerbar, har tillräckligt redaktionellt innehåll och är inte en
 * konto-/formulär-/felsida.
 *
 * Läge styrs av src/lib/ads-config.ts:
 *  - manual : bara <AdSlot />-placeringar
 *  - auto   : bara Auto Ads (page-level), enbart på redaktionella sidor
 *  - hybrid : båda (rekommenderat)
 *
 * Eftersom skriptet bara injiceras på tillåtna sidor kan Auto Ads aldrig
 * placera annonser på start-, fordons-, konto- eller formulärsidor.
 */
export function AutoAds() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const injected = useRef(false);
  const [, force] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!ADS_ENABLED) return;
    if (injected.current) return;

    let cancelled = false;
    // Vänta in render + eventuellt noindex-meta innan vi bedömer sidan.
    const t = window.setTimeout(() => {
      if (cancelled || injected.current) return;
      if (!adScriptAllowedNow(pathname)) return;
      injected.current = true;

      const s = document.createElement("script");
      s.async = true;
      s.crossOrigin = "anonymous";
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${AD_CLIENT}`;
      if (!personalizedAdsAllowed()) s.setAttribute("data-npa", "1");
      s.onload = () => force((n) => n + 1);
      document.head.appendChild(s);
    }, 900);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [pathname]);

  // Auto Ads (page-level): körs bara på redaktionella sidor (blogg, guider,
  // kunskap, campingtips, tester, ordlista, forum m.fl. — se ads-policy.ts).
  // Helsidesformat (vignette/ankare) stängs av i AdSense-panelen under
  // Annonser > Översikt > Automatiska annonser > Annonsformat.
  const autoPushed = useRef<string | null>(null);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!AUTO_ADS_ON) return;
    if (autoPushed.current === pathname) return;
    const t = window.setTimeout(() => {
      if (autoPushed.current === pathname) return;
      if (!autoAdsAllowedNow(pathname)) return;
      autoPushed.current = pathname;
      try {
        window.adsbygoogle = window.adsbygoogle || [];
        window.adsbygoogle.push({
          google_ad_client: AD_CLIENT,
          enable_page_level_ads: true,
        });
      } catch {
        // Skriptet är inte laddat ännu — pushen köas och plockas vid onload.
      }
    }, 1200);
    return () => window.clearTimeout(t);
  }, [pathname]);

  return null;
}
